"use client";

import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Form, FormField, FormItem, FormMessage } from "@shared/src/components/ui/form";
import { CustomCheckbox } from "@shared/src/components/common/form-components/CustomCheckbox";
import { CustomInput } from "@shared/src/components/common/form-components/CustomInput";
import { SearchInput } from "@shared/src/components/common/inputs/SearchInput";
import { CustomTextArea } from "@shared/src/components/common/form-components/CustomTextArea";
import type { PaymentMethod } from "@sdk/shared/types/config.types";
import { configService } from "@shared/src/services/config.service";
import { useErrorContext } from "@shared/src/contexts/ErrorContext";
import { Spinner } from "@shared/src/components/common/misc/Spinner";
import { use, useMemo } from "react";
import { useEffect } from "react";
import { AccountPaymentConfig } from "@sdk/shared/types/config.types";
import CustomDialogue from "@shared/src/components/common/misc/CustomDialogue";
import { useP2PConfig } from "@shared/src/hooks/useP2PConfig";
import { useUserPaymentConfigs } from "@shared/src/hooks/useUserPaymentConfigs";
import { useKycStatus } from "@shared/src/hooks/useKycStatus";
import { IS_MAINNET } from "@shared/src/constants";
import { KycRestriction } from "@shared/src/components/common/dialogs/KycRestriction";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { Button } from "@shared/src/components/ui/button";
import { cn } from "@shared/src/utils/cn";

interface PaymentMethodModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (config: AccountPaymentConfig) => void;
  editingMethod?: AccountPaymentConfig | null;
  preSelectedMethod?: PaymentMethod | null;
  disableMethodSelection?: boolean;
  onServerRefresh?: () => void;
}

const paymentMethodFormSchema = z.object({
  method: z.string().min(1, "Please select a payment method"),
  accountName: IS_MAINNET ? z.string().optional() : z.string().min(1, "Account name is required"),
  accountNumber: z.string().min(1, "Account number is required"),
  extraDetails: z.string().optional(),
});

export type PaymentMethodFormValues = z.infer<typeof paymentMethodFormSchema>;

export function PaymentMethodModal({
  open,
  onOpenChange,
  onSuccess,
  editingMethod,
  preSelectedMethod,
  disableMethodSelection = false,
  onServerRefresh,
}: PaymentMethodModalProps) {
  const [step, setStep] = React.useState<"method" | "details">("method");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { addError } = useErrorContext();
  const { updatePaymentConfigs } = useUserPaymentConfigs();
  const { showModal, closeModal } = useModalStore();

  const { fullName, kycStatus } = useKycStatus();

  // Fetch payment methods using TanStack Query
  const { paymentMethods, isLoading } = useP2PConfig({ paymentMethods: true });

  const form = useForm<PaymentMethodFormValues>({
    resolver: zodResolver(paymentMethodFormSchema),
    defaultValues: editingMethod
      ? {
          method: editingMethod.methodId,
          accountName: editingMethod.accountName,
          accountNumber: editingMethod.account,
          extraDetails: editingMethod.extraInfo,
        }
      : {
          method: "",
          accountName: "",
          accountNumber: "",
          extraDetails: "",
        },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setStep("method");
      if (!editingMethod) {
        form.reset();
      }
    }
  }, [open, form, editingMethod]);

  // Reset form when editingMethod changes
  useEffect(() => {
    if (editingMethod) {
      form.reset({
        method: editingMethod.methodId,
        accountName: editingMethod.accountName,
        accountNumber: editingMethod.account,
        extraDetails: editingMethod.extraInfo,
      });
    }
  }, [editingMethod, form]);

  // Pre-fill the form if we have a preSelectedMethod
  useEffect(() => {
    if (preSelectedMethod) {
      form.setValue("method", preSelectedMethod.id);
      setStep("details");
    }
  }, [preSelectedMethod, form]);

  // Centralized KYC check: if modal is opened and KYC is not approved, close and show warning
  useEffect(() => {
    if (open && IS_MAINNET && kycStatus !== "approved") {
      onOpenChange(false);
      showModal({
        type: "warning",
        title: "KYC Verification Required",
        message: <KycRestriction closeModal={closeModal} />,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kycStatus]);

  const filteredMethods = useMemo(() => {
    if (!searchQuery) return paymentMethods;
    return paymentMethods?.filter(method => method.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery, paymentMethods]);

  const handleSubmit = async (values: PaymentMethodFormValues) => {
    setIsSubmitting(true);
    try {
      const payload = {
        methodId: values.method,
        accountName: IS_MAINNET ? fullName! : values.accountName!,
        account: values.accountNumber,
        extraInfo: values.extraDetails,
      };

      const response = editingMethod
        ? await configService.updatePaymentConfig(editingMethod.id, payload)
        : await configService.createPaymentConfig(payload);

      if (response.error || !response.data) {
        addError({ error: response.error || `Failed to ${editingMethod ? "update" : "save"} payment method` });
        return;
      }

      addError({
        error: null,
        options: {
          successMessage: `Payment method ${editingMethod ? "updated" : "added"} successfully`,
        },
      });

      if (response.data) {
        updatePaymentConfigs([response.data]);
      }

      onOpenChange(false);
      setStep("method");
      form.reset();

      if (onServerRefresh) {
        onServerRefresh();
      }

      if (onSuccess && response.data) {
        onSuccess(response.data);
      }
    } catch (error: any) {
      addError({
        error: error?.message || `Failed to ${editingMethod ? "update" : "save"} payment method`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (IS_MAINNET && kycStatus !== "approved") {
    return null;
  }

  return (
    <CustomDialogue
      open={open}
      onOpenChange={onOpenChange}
      title={editingMethod ? "Edit Payment Method" : step === "method" ? "Payment Method" : "Account Details"}
      headerVariant="inline"
      preventClose={{
        enabled: true,
        hideCloseButton: false,
      }}
    >
      {closeModal => (
        <div className="flex flex-col h-full">
          {step === "details" && (
            <p className="font-extrabold text-h6 text-text-primary text-center mb-4">
              {paymentMethods?.find(m => m.id === form.getValues("method"))?.name}
            </p>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1">
              {step === "method" && !disableMethodSelection ? (
                <>
                  <div className="shrink-0 py-4">
                    <SearchInput
                      value={searchQuery}
                      onChange={setSearchQuery}
                      onClear={() => setSearchQuery("")}
                      placeholder="Search..."
                    />
                  </div>

                  <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="px-2 py-4 space-y-4">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-8 text-text-tertiary">
                          <Spinner size={20} />
                        </div>
                      ) : filteredMethods ? (
                        <FormField
                          control={form.control}
                          name="method"
                          render={({ field }) => (
                            <FormItem className="space-y-4">
                              {filteredMethods.map(method => (
                                <div
                                  key={method.id}
                                  onClick={() => {
                                    field.onChange(method.id);
                                    setStep("details");
                                  }}
                                  className={cn(
                                    "flex items-center justify-between p-4 rounded-xl cursor-pointer bg-surfaces-bg-secondary hover:bg-surfaces-bg-tertiary",
                                    field.value === method.id && "ring-2 ring-border-focus",
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    {method.icon && (
                                      <img
                                        src={method.icon}
                                        alt={method.name}
                                        className="w-5 h-5"
                                        width={20}
                                        height={20}
                                      />
                                    )}
                                    <span className="text-text-primary">{method.name}</span>
                                  </div>
                                  <CustomCheckbox
                                    checked={field.value === method.id}
                                    variant="radio"
                                    className="pointer-events-none"
                                  />
                                </div>
                              ))}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : (
                        <div className="flex items-center justify-center py-8 text-text-tertiary">
                          No payment methods found
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto">
                    <div className="p-4 space-y-6">
                      <FormField
                        control={form.control}
                        name="accountName"
                        render={({ field }) => (
                          <FormItem>
                            {IS_MAINNET ? (
                              <CustomInput
                                disabled
                                label="Account Name"
                                placeholder="Enter account name"
                                error={!!form.formState.errors.accountName}
                                required
                                {...field}
                                value={fullName}
                              />
                            ) : (
                              <CustomInput
                                label="Account Name"
                                placeholder="Enter account name"
                                error={!!form.formState.errors.accountName}
                                required
                                {...field}
                              />
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="accountNumber"
                        render={({ field }) => (
                          <FormItem>
                            <CustomInput
                              label="Account Number"
                              placeholder="Enter account number"
                              error={!!form.formState.errors.accountNumber}
                              required
                              {...field}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="extraDetails"
                        render={({ field }) => (
                          <FormItem>
                            <CustomTextArea
                              label="Extra Details"
                              placeholder="Add extra details eg Bank Name, Bank Branch, etc"
                              error={!!form.formState.errors.extraDetails}
                              {...field}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="shrink-0">
                    <div className="p-4 flex gap-4">
                      {!disableMethodSelection && (
                        <Button
                          type="button"
                          variant="surface-secondary"
                          radius="full"
                          width="flex"
                          onClick={() => setStep("method")}
                          disabled={isSubmitting}
                        >
                          Back
                        </Button>
                      )}
                      <Button type="submit" variant="tertiary" radius="full" width="flex" disabled={isSubmitting}>
                        {isSubmitting ? <Spinner size={20} className="text-current" /> : "Finish"}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </form>
          </Form>
        </div>
      )}
    </CustomDialogue>
  );
}
